<template>
  <div class="card min-width-1250px">
    <header class="bandeau-modal card-header"></header>
    <div class="card-content">
      <header class="has-text-centered column is-12">
        <h3 class="is-size-3 has-text-weight-bold">
          {{ $t('relance.envoiMailMasse.titre') }}
        </h3>
      </header>
      <error-msg-component
        :messages="messagesError"
        :error="messagesError.length > 0"
        @close="messagesError = []"
        title="general.problemOccurred"
        withEmplyFields/>
      <section v-if="!sendEmail">
        <qd-steps
          v-model="activeStep"
          :stepsItems="stepsItems"
          :animated="true"
          :has-navigation="false"
          :destroy-on-hide="true"
          class="step_expanded"
          label-position="bottom">
          <template #selection>
            <relance-mail-tableau :items="items"/>
          </template>
          <template #option>
            <relance-mail-option
              :piecesJointes="piecesJointes"
              :editionsAInclure="editionsAInclure"/>
          </template>
        </qd-steps>
        <footer class="buttons">
          <qd-button
            v-if="activeStep === 0"
            @click="() => $emit('close')"
            class="mgr-2 button btn-modal-annuler"
            type="is-cancel">
            {{ $t('general.button.cancel') }}
          </qd-button>
          <qd-button
            v-else
            @click="actionPrevOrNext(-1)"
            :disabled="activeStep === 0"
            :class="{'button-access-no-permission': activeStep === 0}"
            class="mgr-2 button btn-modal-annuler"
            type="is-cancel">
            {{ $t('general.button.precedent') }}
          </qd-button>
          <qd-button
            v-if="activeStep === numberSteps - 1"
            @click="envoiMails"
            type="is-addOrEdit">
            {{ $t('relance.envoiMailMasse.sendMail') }}
          </qd-button>
          <qd-button
            v-else
            @click="actionPrevOrNext(1)"
            :disabled="activeStep === numberSteps - 1"
            :class="{'button-access-no-permission': activeStep === numberSteps - 1}"
            type="is-addOrEdit">
            {{ $t('general.button.suivant') }}
          </qd-button>
        </footer>
      </section>
      <section v-else
        class="is-flex is-flex-direction-column
        is-justify-content-center is-align-items-center min-height200">
        <section
          class="is-flex is-flex-direction-column
          is-justify-content-center is-align-items-center min-height200">
          <span class="is-size-5">
            {{ isSendingMail
              ? $t('relance.envoiMailMasse.sendingMail')
              : $t('relance.envoiMailMasse.allMailSended') }}
          </span>
          <qd-progress
            :value="nbSend"
            :max="relances.length"
            type="is-success"
            size="is-medium"
            class="width-340 mgt-20px"
            show-value>
            {{ $t('relance.envoiMailMasse.sendedMail', { send: nbSend, total: relances.length }) }}
          </qd-progress>
          <qd-button
            v-if="!isSendingMail"
            @click="() => $emit('close')"
            class="mgt-10px"
            type="is-cancel">
            {{$t('general.button.close')}}
          </qd-button>
        </section>
      </section>
    </div>
  </div>
</template>

<script>
import cloneDeep from 'lodash/cloneDeep';
import { createNamespacedHelpers } from 'vuex';
import { updateEtat } from '@/api/relances/index';
import messageErreur from '@/services/general/messageErreur';
import envoieMailRelance from '@/services/relances/envoieMailRelance';
import { validationMail } from '@/services/relances/validation';
import { removeDuplicateMsgError } from '@/services/validation/validation';
import generationMailEnMasseFromRelances from '@/services/relances/genererMailRelance';
import ErrorMsgComponent from '@/components/general/errorMsgComponent';
import RelanceMailTableau from '@/components/relances/relanceMailEnMasse/relanceMailTableau';
import RelanceMailOption from '@/components/relances/relanceMailEnMasse/relanceMailOption';
import { ENVOYE } from '@/configuration/relances/relancesConf';
import { TAILLE_PJ_LIMITE } from '@/configuration/piecesJointes';

const Societe = createNamespacedHelpers('societe');

export default {
  props: {
    relances: {
      type: Array,
      default: () => [],
    },
    sendingMailInProgress: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    ErrorMsgComponent,
    RelanceMailTableau,
    RelanceMailOption,
  },
  data() {
    return {
      activeStep: 0,
      items: [],
      messagesError: [],
      mail: {},
      editionsAInclure: [
        { libelle: 'relance.popUpMail.inclureFacture', key: 'mettreEdition', value: true },
        { libelle: 'relance.popUpMail.inclureCommande', key: 'mettreEditionCommande', value: true },
      ],
      sendEmail: false,
      nbSend: 0,
      piecesJointes: [],
    };
  },
  computed: {
    ...Societe.mapGetters({
      societesInformations: 'societesInformations',
    }),
    isSendingMail: {
      get() {
        return this.sendingMailInProgress;
      },
      set(value) {
        this.$emit('update:sendingMailInProgress', value);
      },
    },
    stepsItems() {
      return [
        {
          name: 'selection',
          step: 1,
          label: this.$t('relance.envoiMailMasse.steps.selection.titre'),
          clickable: true,
        },
        {
          name: 'option',
          step: 2,
          label: this.$t('relance.envoiMailMasse.steps.option.titre'),
          clickable: this.validationAllMails().length === 0,
        },
      ];
    },
    numberSteps() {
      return this.stepsItems.length;
    },
  },
  methods: {
    actionPrevOrNext(step) {
      const messages = this.validationAllMails();
      if (this.activeStep === 0 && messages.length > 0) {
        this.messagesError = messages;
        return;
      }
      this.activeStep += step;
    },
    cleanAllEmailsRelances() {
      const cleanEmails = (emails) => emails.map(m => m.email || m);
      const relancesToSend = cloneDeep(this.items);
      relancesToSend.forEach(el => {
        el.mail.to = cleanEmails(el.mail.to);
        el.mail.cc = cleanEmails(el.mail.cc);
        el.mail.cci = cleanEmails(el.mail.cci);
      });
      return relancesToSend;
    },
    validationAllMails() {
      this.messagesError = [];
      const messages = [];
      this.cleanAllEmailsRelances().forEach(el => {
        validationMail(el.mail, messages);
      });

      return removeDuplicateMsgError(messages);
    },
    async envoieMail(relance) {
      try {
        relance.mail.piecesJointes = cloneDeep(this.piecesJointes);
        await envoieMailRelance(
          relance.mail,
          this.editionsAInclure[0].value,
          relance.mouvementsClients[0].facture.societeId,
          relance.id,
          this.editionsAInclure[1].value,
        );
        if (!relance.emailDestinataire
          && !relance.nomDestinataire
          && !relance.prenomDestinataire
          && relance.destinataire) {
          await updateEtat({
            etat: ENVOYE,
            relancesIds: [relance.id],
            nomDestinataire: relance.destinataire.nom,
            prenomDestinataire: relance.destinataire.prenom,
            emailDestinataire: relance.destinataire.email,
          });
        }
      } catch (e) {
        messageErreur(this.$i18n);
        throw e;
      }
    },
    async envoiMails() {
      const messages = [];
      if (this.piecesJointes.some(pj => pj.size > TAILLE_PJ_LIMITE)) {
        messages.push({ message: this.$t('relance.envoiMailMasse.error.pjTooBig') });
        this.messagesError = messages;
        return;
      }

      this.sendEmail = true;
      this.isSendingMail = true;

      this.nbSend = 0;
      await this.cleanAllEmailsRelances().reduce(async (promise, relance) => {
        await promise;
        await this.envoieMail(relance);
        this.nbSend += 1;
      }, Promise.resolve());
      this.isSendingMail = false;
    },
  },
  async mounted() {
    const loadingComponent = this.$buefy.loading.open();
    try {
      const relanceMails = await generationMailEnMasseFromRelances(this.relances);
      this.items = cloneDeep(relanceMails);
    } catch (e) {
      messageErreur(this.$i18n);
      throw e;
    } finally {
      loadingComponent.close();
    }
  },
};
</script>
